/**
 * Panorama 360° Module - Panel-Specific Viewer with Fullscreen Support
 *
 * Purpose:
 * - Display interactive 360° panoramas in fullscreen mode
 * - Pannellum-based viewer with custom hotspot system
 * - Panel-specific lifecycle (one viewer per panel)
 * - Fullscreen + scroll blocking when active
 * - LRU=1 cache (max 1 viewer instance)
 * - Mobile-first: orientation controls, touch gestures
 *
 * Module Contract:
 * - name: 'panorama-360'
 * - type: 'panorama'
 * - init(panel): Create viewer when panel activated
 * - start(): Enable controls, start rendering
 * - pause(): Disable controls, stop rendering
 * - stop(): Save camera state, pause
 * - cleanup(): Destroy viewer, free memory
 *
 * HTML Contract:
 * <section class="story-panel">
 *   <div class="pano-slide"
 *        data-pano-src="https://example.com/pano.jpg"
 *        data-pano-yaw="0"
 *        data-pano-pitch="0"
 *        data-pano-hfov="90"
 *        data-pano-debug="0"
 *        data-hotspots='[...]'>
 *     <div class="pano-viewer"></div>
 *   </div>
 * </section>
 *
 * Dependencies: Pannellum, StoryMediaManager, StoryAPI
 * File: assets/js/story-module-panorama-360.js
 */

(function(window) {
  'use strict';

  /* ----------------------------------------------------------
     1. Panorama Module Class
     ---------------------------------------------------------- */

  class PanoramaModule {
    constructor(core) {
      this.core = core;

      // Module Contract Properties
      this.name = 'panorama-360';
      this.type = 'panorama';
      this.isPlaying = false;
      this.panel = null;
      this.element = null; // Current .pano-slide container

      // Viewer Management
      this.currentViewer = null;
      this.currentPanel = null;

      // LRU=1 Cache
      this.cachedViewer = null;
      this.cachedPanel = null;
      this.cachedState = null; // { yaw, pitch, hfov }

      // Fullscreen State
      this.fullscreenActive = false;
      this.scrollWasBlocked = false;

      // Mobile Detection
      this.isMobile = this.detectMobile();

      // Safari Detection (WebGL context loss issues)
      this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

      // Orientation Permission (iOS)
      this.orientationPermission = null;

      // Debug Mode
      this.debugMode = this.checkDebugMode();

      // WebGL Support (check once, cache result)
      this.webglSupported = null;  // null = not checked yet
      this.webglChecked = false;

      // Background Audio (ambient sound for panorama)
      this.backgroundAudio = null;
      this.bgAudioFadeInterval = null;
      this.bgAudioVolume = 0.5;  // Default volume (can be overridden via data-pano-audio-volume)
      this.bgAudioMuted = false;  // Mute state for toggle button

      // Info Tooltip state
      this.currentInfoTooltip = null;
      this.infoTooltipOutsideClickHandler = null;
      this.infoTooltipEscHandler = null;

      // Toolbar state
      this.toolbar = null;

      // Audio Hotspot state (inline player)
      this.currentAudioHotspot = null;
      this.currentAudioElement = null;
      this.audioProgressInterval = null;

      // Initialize
      this.initialize();
    }

    /* ----------------------------------------------------------
       Initialization
       ---------------------------------------------------------- */

    initialize() {
      // Seiten ohne Pano: still inert bleiben (der console.error verwirrte
      // bei jeder Bestandstour, Sichtung 2026-07-23).
      if (!document.querySelector('.pano-slide')) {
        return;
      }

      // Pannellum darf SPÄTER eintreffen (VPN/langsames Netz: die
      // Injection-Datei kann nach DOMContentLoaded landen oder beim ersten
      // Versuch scheitern). Früher: einmalige Prüfung → Modul für immer
      // tot. Jetzt: warnen, Listener trotzdem registrieren — init(panel)
      // wartet bei Bedarf auf die Lib (waitForPannellum).
      if (typeof window.pannellum === 'undefined') {
        console.warn('[Panorama360] Pannellum noch nicht geladen — warte auf die Injection (VPN/langsames Netz?)');
      }

      // Listen to panel events
      document.addEventListener('story:panel:activated', this.handlePanelActivated.bind(this));
      document.addEventListener('story:panel:deactivated', this.handlePanelDeactivated.bind(this));

      // Register with MediaManager
      if (window.StoryMediaManager) {
        window.StoryMediaManager.register(this);

        // AP-B/B3 (F2, 2026-07-23): Audio-Hotspots liefen als rohes
        // new Audio() am MediaManager vorbei (kein Single-Medium, kein
        // Ducking, kein Mute). Eigener Adapter mit type 'audio' — der
        // Viewer selbst bleibt type 'panorama' und rendert weiter, wenn
        // der Manager NUR das Hotspot-Audio pausiert.
        var self = this;
        this.hotspotMedia = {
          name: 'panorama-audio-hotspot',
          type: 'audio',
          init: function () {},   // Manager ruft init(panel) an alle Module
          start: function () {},  // Vertragspflicht (validateModule); Start läuft über handleAudioHotspot
          pause: function () { self.stopAudioHotspot(); },
          stop: function () { self.stopAudioHotspot(); },
          setMuted: function (m) {
            if (self.currentAudioElement) self.currentAudioElement.muted = !!m;
          }
        };
        window.StoryMediaManager.register(this.hotspotMedia);

        if (this.debugMode) {
          console.log('[Panorama360] Registered with MediaManager (+ Hotspot-Audio-Adapter)');
        }
      }

      // Register with core
      if (this.core) {
        this.core.registerModule('panorama360', this);
      }
    }

    /* ----------------------------------------------------------
       Mobile & Debug Detection
       ---------------------------------------------------------- */

    detectMobile() {
      // Check both screen size AND user agent
      // Modern laptops with touchscreens should NOT be treated as mobile
      var mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      var smallScreen = window.innerWidth <= 768;

      // Only return true if BOTH conditions suggest mobile
      return mobileUA || (smallScreen && ('ontouchstart' in window));
    }

    checkDebugMode() {
      // Check URL param or global flag
      return (
        window.location.search.includes('panoDebug=1') ||
        window.location.search.includes('debug=story')
      );
    }

    /* ----------------------------------------------------------
       Panel Event Handlers
       ---------------------------------------------------------- */

    handlePanelActivated(e) {
      var panel = e.detail.panel;

      // Check if panel contains .pano-slide
      var panoSlide = panel.querySelector('.pano-slide');
      if (!panoSlide) {
        // Not a panorama panel - this is normal, don't warn
        return;
      }

      if (this.debugMode) {
        console.log('[Panorama360] Panel activated with panorama:', e.detail.index);
      }

      // Delay initialization to ensure DOM is ready and sized
      // Safari needs longer delay due to WebGL context initialization issues
      var delay = this.isSafari ? 500 : 100;
      var self = this;

      if (this.debugMode) {
        console.log('[Panorama360] Delaying init by', delay + 'ms', '(Safari:', this.isSafari + ')');
      }

      setTimeout(function() {
        self.init(panel);
      }, delay);
    }

    handlePanelDeactivated(e) {
      var panel = e.detail.panel;

      // Check if this is the current panorama panel
      if (panel !== this.currentPanel) {
        return;
      }

      if (this.debugMode) {
        console.log('[Panorama360] Panel deactivated:', e.detail.index);
      }

      // Stop viewer
      this.stop();
    }

    /* ----------------------------------------------------------
       Module Contract Methods
       ---------------------------------------------------------- */

    /**
     * Wartet auf die Pannellum-Lib (bis ~30s, 500ms-Raster) und ruft dann
     * den Callback — deckt langsam nachladende oder per Fallback erneut
     * angeforderte Injections ab.
     */
    waitForPannellum(callback) {
      if (typeof window.pannellum !== 'undefined') { callback(); return; }
      var self = this;
      var versuche = 0;
      if (this._pannellumPoll) clearInterval(this._pannellumPoll);
      this._pannellumPoll = setInterval(function() {
        versuche++;
        if (typeof window.pannellum !== 'undefined') {
          clearInterval(self._pannellumPoll);
          self._pannellumPoll = null;
          if (self.debugMode) console.log('[Panorama360] Pannellum nachträglich eingetroffen');
          callback();
        } else if (versuche > 60) {
          clearInterval(self._pannellumPoll);
          self._pannellumPoll = null;
          console.error('[Panorama360] Pannellum library not found! Please include pannellum.js');
        }
      }, 500);
    }

    init(panel) {
      // Called when panorama panel is activated

      // Pannellum ggf. noch unterwegs → warten und erneut versuchen
      if (typeof window.pannellum === 'undefined') {
        var self = this;
        this.waitForPannellum(function() { self.init(panel); });
        return;
      }

      // GUARD: Prevent double initialization (can happen with rapid scroll or event bubbling)
      if (this.currentPanel === panel && this.currentViewer) {
        if (this.debugMode) {
          console.log('[Panorama360] Panel already initialized, skipping duplicate init');
        }
        return;
      }

      this.panel = panel;
      this.currentPanel = panel;

      var panoSlide = panel.querySelector('.pano-slide');
      if (!panoSlide) {
        if (this.debugMode) console.log('[Panorama360] No .pano-slide found in panel'); // war console.warn — rauschte auf jeder Panel-Aktivierung (Sichtung 2026-08-19)
        return;
      }

      this.element = panoSlide;

      // CRITICAL FIX: Override .story-panel__content max-width constraint
      // .story-panel__content is designed for text panels (max-width: 800px)
      // Panoramas need full-width, so we add a class to override
      var contentWrapper = panel.querySelector('.story-panel__content');
      if (contentWrapper) {
        contentWrapper.classList.add('pano-container');
      }

      // Remove padding from parent .story-panel for fullscreen
      panel.classList.add('pano-panel');

      if (this.debugMode) {
        console.log('[Panorama360] Applied fullscreen overrides (no padding, no max-width)');
      }

      // Check if this is the cached panel
      if (this.cachedPanel === panel && this.cachedViewer) {
        if (this.debugMode) {
          console.log('[Panorama360] Restoring cached viewer');
        }

        // Restore from cache
        this.currentViewer = this.cachedViewer;
        this.restoreCameraState(this.currentViewer, this.cachedState);

        // Clear cache (move to current)
        this.cachedViewer = null;
        this.cachedPanel = null;
        this.cachedState = null;

        // AP-B (V5c): Re-Apply-Haken nach LRU-Restore — das Hotspot-DOM blieb
        // erhalten, aber Zustände (besucht/aktuell) können veraltet sein.
        document.dispatchEvent(new CustomEvent('pano:hotspots:ready', {
          detail: { slide: panoSlide, viewer: this.currentViewer, restored: true }
        }));
      } else {
        // Destroy cached viewer (LRU=1 eviction)
        if (this.cachedViewer) {
          if (this.debugMode) {
            console.log('[Panorama360] Evicting cached viewer');
          }
          this.destroyViewer(this.cachedViewer);
          this.cachedViewer = null;
          this.cachedPanel = null;
          this.cachedState = null;
        }

        // Create new viewer
        this.currentViewer = this.createViewer(panoSlide);
      }

      // Enable fullscreen (removes padding, applies classes)
      this.enableFullscreen(panoSlide);

      // CRITICAL: Resize viewer after fullscreen changes container size
      // Without this, zoom won't work visually (viewer thinks container is still small)
      if (this.currentViewer && typeof this.currentViewer.resize === 'function') {
        // Longer delay to ensure CSS changes are fully applied
        // Safari especially needs more time for layout recalculation
        var self = this;
        var resizeDelay = this.isSafari ? 200 : 100;

        setTimeout(function() {
          // Log container size BEFORE resize
          var viewerContainer = panoSlide.querySelector('.pano-viewer');
          if (self.debugMode && viewerContainer) {
            var rectBefore = viewerContainer.getBoundingClientRect();
            console.log('[Panorama360] Container size BEFORE resize:', rectBefore.width + 'x' + rectBefore.height);
          }

          self.currentViewer.resize();

          // Log container size AFTER resize
          if (self.debugMode && viewerContainer) {
            var rectAfter = viewerContainer.getBoundingClientRect();
            console.log('[Panorama360] Container size AFTER resize:', rectAfter.width + 'x' + rectAfter.height);
            console.log('[Panorama360] Viewport size:', window.innerWidth + 'x' + window.innerHeight);
          }

          if (self.debugMode) {
            console.log('[Panorama360] Viewer resized after fullscreen');
          }
        }, resizeDelay);
      }

      // Start viewer
      this.start();
    }

    start() {
      // Enable controls and rendering
      if (!this.currentViewer) return;

      this.isPlaying = true;

      // Duck-Leck-Fix (Live-Befund 2026-07-24): requestPlay NUR, wenn das
      // Pano wirklich eigenes Hintergrund-Audio hat. Ein stummes Panorama,
      // das sich als „spielendes Medium" anmeldet, duckte die globale
      // Tour-Atmo dauerhaft auf duckVolume, solange man am Hub stand —
      // ohne dass irgendetwas zu hören war. (Hotspot-Audio routet ohnehin
      // separat über hotspotMedia.)
      if (window.StoryMediaManager && this.element && this.element.dataset.panoAudioBg) {
        window.StoryMediaManager.requestPlay(this);
      }

      // Start background audio if configured
      this.startBackgroundAudio();

      if (this.debugMode) {
        console.log('[Panorama360] Viewer started');
      }
    }

    pause() {
      // Pause rendering (called by MediaManager when another medium starts)
      if (!this.currentViewer) return;

      this.isPlaying = false;

      if (this.debugMode) {
        console.log('[Panorama360] Viewer paused');
      }

      // Note: We don't destroy the viewer, just mark as paused
      // Viewer keeps rendering (can't fully pause Pannellum)
    }

    stop() {
      // Stop viewer and save state (called when panel deactivates)
      if (!this.currentViewer) return;

      this.isPlaying = false;

      // Stop background audio with fade-out
      this.stopBackgroundAudio(true);  // true = use fade

      // Stop any playing audio hotspot
      this.stopAudioHotspot();

      // Close any open info tooltip
      this.closeInfoTooltip();

      // Save camera state
      var state = this.saveCameraState(this.currentViewer);

      // Move to cache (LRU=1)
      this.cachedViewer = this.currentViewer;
      this.cachedPanel = this.currentPanel;
      this.cachedState = state;

      // Clear current
      this.currentViewer = null;
      this.currentPanel = null;
      this.element = null;
      this.panel = null;

      // Disable fullscreen
      this.disableFullscreen();

      // Notify MediaManager
      if (window.StoryMediaManager) {
        window.StoryMediaManager.notifyStop(this);
      }

      if (this.debugMode) {
        console.log('[Panorama360] Viewer stopped and cached');
      }
    }

    cleanup() {
      // Free all resources (called on panel deactivation if needed)
      if (this.currentViewer) {
        this.destroyViewer(this.currentViewer);
        this.currentViewer = null;
      }

      if (this.cachedViewer) {
        this.destroyViewer(this.cachedViewer);
        this.cachedViewer = null;
        this.cachedPanel = null;
        this.cachedState = null;
      }

      // Stop background audio immediately (no fade during cleanup)
      this.stopBackgroundAudio(false);

      this.disableFullscreen();

      // Remove full-width classes from wrappers
      if (this.panel) {
        var contentWrapper = this.panel.querySelector('.story-panel__content');
        if (contentWrapper) {
          contentWrapper.classList.remove('pano-container');
        }
        this.panel.classList.remove('pano-panel');
      }

      this.isPlaying = false;
      this.panel = null;
      this.element = null;

      if (this.debugMode) {
        console.log('[Panorama360] Cleanup complete');
      }
    }

    /* ----------------------------------------------------------
       Viewer Lifecycle
       ---------------------------------------------------------- */

    createViewer(panoSlide) {
      var viewerContainer = panoSlide.querySelector('.pano-viewer');
      if (!viewerContainer) {
        console.error('[Panorama360] .pano-viewer container not found in .pano-slide');
        return null;
      }

      // Check if container has size (common issue causing WebGL errors)
      var rect = viewerContainer.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        console.error('[Panorama360] Viewer container has no size! width:', rect.width, 'height:', rect.height);
        console.error('[Panorama360] Make sure .pano-slide is visible and has dimensions');
        return null;
      }

      // Optional WebGL check for debug logging (Pannellum will do its own check)
      // Skip on Safari due to context loss issues - let Pannellum handle it
      if (this.debugMode && !this.isSafari) {
        var webglOk = this.isWebGLSupported();
        console.log('[Panorama360] WebGL pre-check:', webglOk ? 'OK' : 'Failed (will try anyway)');
      } else if (this.debugMode && this.isSafari) {
        console.log('[Panorama360] Safari detected - skipping WebGL pre-check, letting Pannellum handle it');
      }

      // Parse config from data attributes
      var config = this.parseConfig(panoSlide);

      // Parse hotspots
      var hotspots = this.parseHotspots(panoSlide);
      if (hotspots && hotspots.length > 0) {
        config.hotSpots = hotspots;
      }

      // Debug mode
      if (this.debugMode || panoSlide.dataset.panoDebug === '1') {
        config.hotSpotDebug = true;
      }

      if (this.debugMode) {
        console.log('[Panorama360] Creating viewer with config:', config);
        console.log('[Panorama360] Container size:', rect.width + 'x' + rect.height);
      }

      // Create viewer
      try {
        var viewer = window.pannellum.viewer(viewerContainer, config);

        if (this.debugMode) {
          console.log('[Panorama360] Viewer created successfully');
        }

        // Add custom zoom controls (+/- buttons)
        this.addZoomControls(viewerContainer, viewer);

        // Add toolbar (info + audio mute buttons)
        this.addToolbar(viewerContainer, panoSlide);

        // AP-B (V5c): "ready"-Event, sobald Pannellum das Pano geladen hat —
        // die Hotspot-DOM-Knoten (inkl. data-hotspot-id) existieren dann.
        if (typeof viewer.on === 'function') {
          viewer.on('error', function() {
            try { viewerContainer.classList.add('is-loaded'); panoSlide.classList.add('is-loaded'); } catch (e) {} // Fehlertext sichtbar machen
          });
          viewer.on('load', function() {
            // Sichtung 2026-08-18: Lade-Label/Spinner erst jetzt weg, Renderer weich einblenden (CSS .is-loaded)
            try { viewerContainer.classList.add('is-loaded'); panoSlide.classList.add('is-loaded'); } catch (e) {}
            document.dispatchEvent(new CustomEvent('pano:hotspots:ready', {
              detail: { slide: panoSlide, viewer: viewer, restored: false }
            }));
          });
        }

        return viewer;
      } catch (error) {
        console.error('[Panorama360] Failed to create viewer:', error);
        console.error('[Panorama360] Config was:', config);
        console.error('[Panorama360] Container:', viewerContainer);

        // Show user-friendly error
        this.showErrorMessage(viewerContainer, 'Panorama konnte nicht geladen werden: ' + error.message);

        return null;
      }
    }

    addZoomControls(container, viewer) {
      // Check if controls already exist
      if (container.querySelector('.pano-zoom-controls')) {
        return;
      }

      // Create zoom controls container
      var controls = document.createElement('div');
      controls.className = 'pano-zoom-controls';

      // Zoom In button
      var zoomIn = document.createElement('button');
      zoomIn.className = 'pano-zoom-btn pano-zoom-btn--in';
      zoomIn.innerHTML = '+';
      zoomIn.title = 'Zoom In';
      zoomIn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('[Panorama360] Zoom In clicked');
        try {
          var hfov = viewer.getHfov();
          console.log('[Panorama360] Current HFOV:', hfov);
          viewer.setHfov(Math.max(hfov - 10, 65)); // Min 65° to prevent quality issues
          console.log('[Panorama360] New HFOV:', Math.max(hfov - 10, 65));
        } catch (err) {
          console.error('[Panorama360] Zoom In failed:', err);
        }
      });

      // Zoom Out button
      var zoomOut = document.createElement('button');
      zoomOut.className = 'pano-zoom-btn pano-zoom-btn--out';
      zoomOut.innerHTML = '−';
      zoomOut.title = 'Zoom Out';
      zoomOut.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var hfov = viewer.getHfov();
        viewer.setHfov(Math.min(hfov + 10, 120)); // Max 120°
      });

      controls.appendChild(zoomIn);
      controls.appendChild(zoomOut);

      // Append to container (will be positioned via CSS)
      container.appendChild(controls);

      if (this.debugMode) {
        console.log('[Panorama360] Custom zoom controls added');
      }
    }

    /**
     * Add toolbar with Info and Audio Mute buttons (fixed position, top-left)
     */
    addToolbar(container, panoSlide) {
      // Check if toolbar already exists
      if (container.querySelector('.pano-toolbar')) {
        return;
      }

      var self = this;

      // Create toolbar container
      var toolbar = document.createElement('div');
      toolbar.className = 'pano-toolbar';

      // --- Info Button ---
      var infoTitle = panoSlide.dataset.panoInfoTitle;
      var infoText = panoSlide.dataset.panoInfoText;
      var hasInfo = infoTitle || infoText;

      if (hasInfo) {
        var infoBtn = document.createElement('button');
        infoBtn.className = 'pano-toolbar__btn pano-toolbar__btn--info';
        infoBtn.setAttribute('aria-label', 'Info anzeigen');
        infoBtn.innerHTML = '<svg class="pano-toolbar__icon" aria-hidden="true"><use href="#icon-info"></use></svg>';
        infoBtn.title = 'Info';

        infoBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          self.showToolbarInfo(infoTitle || 'Info', infoText || '');
        });

        toolbar.appendChild(infoBtn);
      }

      // --- Audio Mute Button ---
      var hasAudio = panoSlide.dataset.panoAudioBg;

      if (hasAudio) {
        var audioBtn = document.createElement('button');
        audioBtn.className = 'pano-toolbar__btn pano-toolbar__btn--audio';
        audioBtn.setAttribute('aria-label', 'Audio stumm schalten');
        audioBtn.innerHTML = '<svg class="pano-toolbar__icon pano-toolbar__icon--unmuted" aria-hidden="true"><use href="#icon-audio"></use></svg>' +
                             '<svg class="pano-toolbar__icon pano-toolbar__icon--muted" aria-hidden="true" style="display:none"><use href="#icon-audio-mute"></use></svg>';
        audioBtn.title = 'Audio stumm schalten';

        audioBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          self.toggleBackgroundAudioMute(audioBtn);
        });

        toolbar.appendChild(audioBtn);
        this.audioMuteButton = audioBtn;
      }

      // Only add toolbar if it has buttons
      if (toolbar.children.length > 0) {
        container.appendChild(toolbar);
        this.toolbar = toolbar;

        if (this.debugMode) {
          console.log('[Panorama360] Toolbar added (info:', hasInfo, ', audio:', !!hasAudio, ')');
        }
      }
    }

    /**
     * Show info tooltip from toolbar button
     */
    showToolbarInfo(title, text) {
      // Close any existing tooltip
      this.closeInfoTooltip();

      // Create tooltip element
      var tooltip = document.createElement('div');
      tooltip.className = 'pano-info-tooltip';
      tooltip.innerHTML =
        '<div class="pano-info-tooltip__header">' +
          '<span class="pano-info-tooltip__title">' + this.escapeHtml(title) + '</span>' +
          '<button class="pano-info-tooltip__close" aria-label="Schließen">×</button>' +
        '</div>' +
        '<div class="pano-info-tooltip__content">' + this.escapeHtml(text) + '</div>';

      // Add to DOM
      document.body.appendChild(tooltip);
      this.currentInfoTooltip = tooltip;

      // Close button handler
      var self = this;
      var closeBtn = tooltip.querySelector('.pano-info-tooltip__close');
      closeBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        self.closeInfoTooltip();
      });

      // Outside click handler (delayed to prevent immediate close)
      setTimeout(function() {
        self.infoTooltipOutsideClickHandler = function(e) {
          if (!tooltip.contains(e.target)) {
            self.closeInfoTooltip();
          }
        };
        document.addEventListener('click', self.infoTooltipOutsideClickHandler);
      }, 100);

      // ESC key handler
      self.infoTooltipEscHandler = function(e) {
        if (e.key === 'Escape') {
          self.closeInfoTooltip();
        }
      };
      document.addEventListener('keydown', self.infoTooltipEscHandler);

      // Animate in
      requestAnimationFrame(function() {
        tooltip.classList.add('pano-info-tooltip--visible');
      });

      if (this.debugMode) {
        console.log('[Panorama360] Toolbar info opened:', title);
      }
    }

    /**
     * Toggle background audio mute/unmute
     */
    toggleBackgroundAudioMute(button) {
      if (!this.backgroundAudio) return;

      this.bgAudioMuted = !this.bgAudioMuted;

      // Toggle icons
      var unmutedIcon = button.querySelector('.pano-toolbar__icon--unmuted');
      var mutedIcon = button.querySelector('.pano-toolbar__icon--muted');

      if (this.bgAudioMuted) {
        // Mute: fade out and pause
        this.pauseBackgroundAudio();
        button.classList.add('pano-toolbar__btn--muted');
        button.setAttribute('aria-label', 'Audio einschalten');
        button.title = 'Audio einschalten';

        // Show muted icon, hide unmuted
        if (unmutedIcon) unmutedIcon.style.display = 'none';
        if (mutedIcon) mutedIcon.style.display = '';

        if (this.debugMode) {
          console.log('[Panorama360] Background audio muted by user');
        }
      } else {
        // Unmute: resume with fade in
        this.resumeBackgroundAudio();
        button.classList.remove('pano-toolbar__btn--muted');
        button.setAttribute('aria-label', 'Audio stumm schalten');
        button.title = 'Audio stumm schalten';

        // Show unmuted icon, hide muted
        if (unmutedIcon) unmutedIcon.style.display = '';
        if (mutedIcon) mutedIcon.style.display = 'none';

        if (this.debugMode) {
          console.log('[Panorama360] Background audio unmuted by user');
        }
      }
    }

    destroyViewer(viewer) {
      if (!viewer) return;

      try {
        // Pannellum destroy method
        if (typeof viewer.destroy === 'function') {
          viewer.destroy();
        }

        if (this.debugMode) {
          console.log('[Panorama360] Viewer destroyed');
        }
      } catch (error) {
        console.error('[Panorama360] Error destroying viewer:', error);
      }
    }

    /* ----------------------------------------------------------
       Configuration Parsing
       ---------------------------------------------------------- */

    parseConfig(panoSlide) {
      // Responsive image source (desktop vs mobile)
      var panoramaSrc = panoSlide.dataset.panoSrc || '';

      if (this.debugMode) {
        console.log('[Panorama360] Image source selection:');
        console.log('  - isMobile:', this.isMobile);
        console.log('  - data-pano-src:', panoSlide.dataset.panoSrc || '(not set)');
        console.log('  - data-pano-src-desktop:', panoSlide.dataset.panoSrcDesktop || '(not set)');
        console.log('  - data-pano-src-mobile:', panoSlide.dataset.panoSrcMobile || '(not set)');
      }

      // Check for device-specific sources
      if (this.isMobile && panoSlide.dataset.panoSrcMobile) {
        panoramaSrc = panoSlide.dataset.panoSrcMobile;
        if (this.debugMode) {
          console.log('  → Using MOBILE source:', panoramaSrc);
        }
      } else if (!this.isMobile && panoSlide.dataset.panoSrcDesktop) {
        panoramaSrc = panoSlide.dataset.panoSrcDesktop;
        if (this.debugMode) {
          console.log('  → Using DESKTOP source:', panoramaSrc);
        }
      } else {
        if (this.debugMode) {
          console.log('  → Using FALLBACK source:', panoramaSrc);
        }
      }

      var config = {
        type: 'equirectangular',
        panorama: panoramaSrc,
        autoLoad: true,

        // Initial camera position (all in degrees)
        yaw: parseFloat(panoSlide.dataset.panoYaw) || 0,
        pitch: parseFloat(panoSlide.dataset.panoPitch) || 0,
        hfov: parseFloat(panoSlide.dataset.panoHfov) || 90,

        // Mobile: Orientation controls on by default
        orientationOnByDefault: this.isMobile,

        // Constraints - limit zoom to prevent quality issues with lower-res source images
        // minHfov = max zoom IN (smaller = more zoom, shows pixelation)
        // maxHfov = max zoom OUT (larger = wider view)
        minHfov: 65,  // Prevent zooming in too far (was 30)
        maxHfov: 120,

        // Auto-rotate disabled (user controls)
        autoRotate: false,

        // Disable double-click zoom (can conflict with touch)
        doubleClickZoom: false,

        // Show controls
        showControls: true,
        showFullscreenCtrl: false, // We handle fullscreen via CSS
        showZoomCtrl: false, // We add custom zoom controls (+/- buttons)

        // Accessibility
        keyboardZoom: true,
        mouseZoom: false, // DISABLED: Scroll is for panel navigation, not zoom
        draggable: true, // Mouse drag for panning (keep enabled)

        // Quality: Use dynamic updates for smooth zooming
        // Note: Pannellum uses the full source image resolution
        // Quality depends on the source image size (recommend 8192x4096 or higher)
        dynamicUpdate: true
      };

      // AP-B (Pano-Spec §5a-Staffelung): bounded Pro-Pano-Flags aus pano-tool-1.
      // Ohne Attribut bleibt exakt das bisherige Verhalten (Bestandstouren).
      var autoRotate = parseFloat(panoSlide.dataset.panoAutoRotate);
      if (!isNaN(autoRotate) && autoRotate !== 0) {
        config.autoRotate = autoRotate;
      }
      if (panoSlide.dataset.panoGyro === '0') {
        config.orientationOnByDefault = false;
      }
      if (panoSlide.dataset.panoZoomWheel === '1') {
        config.mouseZoom = true;
      }

      return config;
    }

    /* ----------------------------------------------------------
       Hotspot Parsing
       ---------------------------------------------------------- */

    parseHotspots(panoSlide) {
      var hotspotsAttr = panoSlide.dataset.hotspots;
      if (!hotspotsAttr) {
        return null;
      }

      try {
        var hotspots = JSON.parse(hotspotsAttr);
        var self = this;

        // Hotspot limit warning (soft limit, not hard fail)
        var HOTSPOT_LIMIT = 7;
        if (hotspots.length > HOTSPOT_LIMIT) {
          console.warn('[Panorama360] Too many hotspots (' + hotspots.length + ')! Recommended max: ' + HOTSPOT_LIMIT);
          console.warn('[Panorama360] Consider reducing hotspots for better UX (avoid "Christmas tree" panoramas)');
        }

        // Convert to Pannellum format
        return hotspots.map(function(hs) {
          return self.convertHotspotConfig(hs);
        });
      } catch (error) {
        console.error('[Panorama360] Invalid hotspots JSON:', error);
        return null;
      }
    }

    convertHotspotConfig(hotspot) {
      var self = this;

      // Pannellum hotspot config
      var config = {
        pitch: hotspot.pitch,
        yaw: hotspot.yaw,
        type: 'info', // Pannellum type (we override with custom tooltip)
        scale: true,  // Auto-scale with FOV

        // Custom tooltip rendering
        createTooltipFunc: function(hotSpotDiv, args) {
          return self.createHotspotTooltip(hotSpotDiv, args);
        },
        createTooltipArgs: hotspot,

        // Click handler
        clickHandlerFunc: function(event, args) {
          self.handleHotspotClick(event, args);
        },
        clickHandlerArgs: hotspot
      };

      return config;
    }

    createHotspotTooltip(hotSpotDiv, args) {
      // Create custom hotspot DOM
      hotSpotDiv.classList.add('pano-hotspot');
      hotSpotDiv.classList.add('pano-hotspot--' + args.type);

      // Size variant (small, large, xlarge)
      if (args.size) {
        hotSpotDiv.classList.add('pano-hotspot--' + args.size);
      }

      // AP-B (V5c): id + Kapitel-Ziel + Marker-Variante stempeln — der
      // Gegenpart (Zustände besucht/aktuell) ist die sg-Marker-Brücke in
      // story-module-sperrgebiet.js (sg:updated / pano:hotspots:ready).
      if (args.id !== undefined && args.id !== null && String(args.id) !== '') {
        hotSpotDiv.setAttribute('data-hotspot-id', String(args.id));
      }
      if (args.type === 'goto' && args.targetChapter !== undefined &&
          args.targetChapter !== null && String(args.targetChapter) !== '') {
        hotSpotDiv.setAttribute('data-target-chapter', String(args.targetChapter));
      }
      if (args.marker && /^[a-z][a-z0-9-]*$/i.test(String(args.marker))) {
        hotSpotDiv.classList.add('pano-hotspot--marker-' + String(args.marker));
      }

      // SVG Icon (must use createElementNS for proper SVG handling)
      var iconWrapper = document.createElement('span');
      iconWrapper.className = 'pano-hotspot__icon';

      var svg = this.createSVGIcon(args.type, args);
      iconWrapper.appendChild(svg);
      hotSpotDiv.appendChild(iconWrapper);

      // Title (if provided) - shown as floating tooltip on hover.
      // pano-tool-1 exportiert das Feld als "label" (Daten-Vertrag §1).
      var titleText = args.title || args.label;
      if (titleText) {
        var title = document.createElement('span');
        title.className = 'pano-hotspot__title';
        title.textContent = titleText;
        hotSpotDiv.appendChild(title);
      }

      return hotSpotDiv;
    }

    /**
     * Create SVG icon element with proper namespace
     * innerHTML doesn't work well with SVG - must use createElementNS
     */
    createSVGIcon(type, args) {
      var SVG_NS = 'http://www.w3.org/2000/svg';
      var XLINK_NS = 'http://www.w3.org/1999/xlink';

      // Map type to icon ID
      var iconIds = {
        audio: 'icon-audio',
        image: 'icon-image',
        video: 'icon-video',
        goto: 'icon-goto',
        action: 'icon-action',
        info: 'icon-info'
      };

      var iconId = iconIds[type] || 'icon-info';
      // Opt-in (Spurensuche 2026-08-18): eigenes Symbol je Hotspot, z. B. "icon":"pano"
      // für „nächstes Panorama" — nur wenn #icon-<name> im Dokument existiert.
      if (args && typeof args.icon === 'string' && /^[a-z0-9-]+$/.test(args.icon) &&
          document.getElementById('icon-' + args.icon)) {
        iconId = 'icon-' + args.icon;
      }

      // Check if symbol exists in DOM
      var symbolExists = document.getElementById(iconId);
      if (this.debugMode) {
        console.log('[Panorama360] Creating icon for type:', type, 'symbolExists:', !!symbolExists);
      }

      // Create SVG element with proper namespace
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      svg.style.width = '24px';
      svg.style.height = '24px';
      svg.style.display = 'block';

      // Create use element with proper namespace
      var use = document.createElementNS(SVG_NS, 'use');
      use.setAttributeNS(XLINK_NS, 'xlink:href', '#' + iconId);
      // Also set href for modern browsers
      use.setAttribute('href', '#' + iconId);

      svg.appendChild(use);
      return svg;
    }

    /* ----------------------------------------------------------
       Hotspot Click Handlers
       ---------------------------------------------------------- */

    handleHotspotClick(event, args) {
      if (this.debugMode) {
        console.log('[Panorama360] Hotspot clicked:', args);
      }

      // Store clicked hotspot element for audio handler
      this._lastClickedHotspot = event.target.closest('.pano-hotspot');

      // Validate type
      var handler = this.getHotspotHandler(args.type);
      if (!handler) {
        console.warn('[Panorama360] Unknown hotspot type:', args.type);
        return;
      }

      // Execute handler
      handler.call(this, args);
    }

    getHotspotHandler(type) {
      var handlers = {
        audio: this.handleAudioHotspot,
        image: this.handleImageHotspot,
        video: this.handleVideoHotspot,
        goto: this.handleGotoHotspot,
        action: this.handleActionHotspot,
        info: this.handleInfoHotspot
      };
      return handlers[type];
    }

    handleAudioHotspot(args, hotspotElement) {
      if (this.debugMode) {
        console.log('[Panorama360] Audio hotspot clicked:', args);
      }

      // Validate source
      if (!args.src) {
        console.error('[Panorama360] Audio hotspot missing src!');
        return;
      }

      // User Gesture Gate
      if (window.StoryMediaManager && !window.StoryMediaManager.userGestureReceived) {
        window.StoryMediaManager.userGestureReceived = true;
      }

      // Find the hotspot element if not passed
      if (!hotspotElement) {
        // Try to find it via the event target (set in click handler)
        hotspotElement = this._lastClickedHotspot;
      }

      // Check if this is the currently playing hotspot - toggle play/pause
      if (this.currentAudioHotspot === hotspotElement && this.currentAudioElement) {
        if (this.currentAudioElement.paused) {
          // Resume - remove paused state, add playing state
          this.currentAudioElement.play();
          hotspotElement.classList.remove('pano-hotspot--paused');
          hotspotElement.classList.add('pano-hotspot--playing');
          if (this.debugMode) {
            console.log('[Panorama360] Audio resumed');
          }
        } else {
          // Pause - remove playing state, add paused state
          this.currentAudioElement.pause();
          hotspotElement.classList.remove('pano-hotspot--playing');
          hotspotElement.classList.add('pano-hotspot--paused');
          if (this.debugMode) {
            console.log('[Panorama360] Audio paused');
          }
        }
        return;
      }

      // Stop any currently playing audio hotspot
      this.stopAudioHotspot();

      // AP-B/B3 (F2): Single-Medium + Ducking über den Manager — pausiert
      // laufenden O-Ton/Bubbles/Galerie und duckt die Atmo.
      if (window.StoryMediaManager && this.hotspotMedia) {
        if (!window.StoryMediaManager.requestPlay(this.hotspotMedia)) return;
      }

      // Pause background audio
      this.pauseBackgroundAudio();

      // Create audio element
      var audio = new Audio(args.src);
      audio.preload = 'auto';
      audio.muted = !!this._globalMuted; // globaler Mute (B3)

      this.currentAudioElement = audio;
      this.currentAudioHotspot = hotspotElement;

      // Add progress ring to hotspot if not exists
      this.addProgressRing(hotspotElement);

      // Setup audio events
      var self = this;

      audio.addEventListener('timeupdate', function() {
        self.updateProgressRing(hotspotElement, audio);
      });

      audio.addEventListener('ended', function() {
        self.stopAudioHotspot();
        self.resumeBackgroundAudio();
        if (self.debugMode) {
          console.log('[Panorama360] Audio ended');
        }
      });

      audio.addEventListener('error', function(e) {
        console.error('[Panorama360] Audio error:', e);
        self.stopAudioHotspot();
        self.resumeBackgroundAudio();
      });

      // Start playback
      hotspotElement.classList.add('pano-hotspot--playing');
      hotspotElement.classList.remove('pano-hotspot--paused');

      audio.play().then(function() {
        if (self.debugMode) {
          console.log('[Panorama360] Audio started:', args.src);
        }
      }).catch(function(err) {
        console.error('[Panorama360] Audio play failed:', err);
        self.stopAudioHotspot();
        // B3 (Ultrareview bug_004): auch im Fehlerfall die Atmo zurückholen
        self.resumeBackgroundAudio();
      });
    }

    /**
     * Add progress ring SVG to hotspot
     */
    addProgressRing(hotspotElement) {
      // Check if already has progress ring
      if (hotspotElement.querySelector('.pano-hotspot__progress')) {
        return;
      }

      var SVG_NS = 'http://www.w3.org/2000/svg';

      // Create progress ring SVG
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'pano-hotspot__progress');
      svg.setAttribute('viewBox', '0 0 52 52');

      // Background circle
      var bgCircle = document.createElementNS(SVG_NS, 'circle');
      bgCircle.setAttribute('class', 'pano-hotspot__progress-bg');
      bgCircle.setAttribute('cx', '26');
      bgCircle.setAttribute('cy', '26');
      bgCircle.setAttribute('r', '24');

      // Progress circle
      var progressCircle = document.createElementNS(SVG_NS, 'circle');
      progressCircle.setAttribute('class', 'pano-hotspot__progress-ring');
      progressCircle.setAttribute('cx', '26');
      progressCircle.setAttribute('cy', '26');
      progressCircle.setAttribute('r', '24');

      // Calculate circumference
      var circumference = 2 * Math.PI * 24;
      progressCircle.style.strokeDasharray = circumference;
      progressCircle.style.strokeDashoffset = circumference;

      svg.appendChild(bgCircle);
      svg.appendChild(progressCircle);

      // Insert at beginning of hotspot
      hotspotElement.insertBefore(svg, hotspotElement.firstChild);
    }

    /**
     * Update progress ring based on audio progress
     */
    updateProgressRing(hotspotElement, audio) {
      var progressRing = hotspotElement.querySelector('.pano-hotspot__progress-ring');
      if (!progressRing || !audio.duration) return;

      var circumference = 2 * Math.PI * 24;
      var progress = audio.currentTime / audio.duration;
      var offset = circumference * (1 - progress);

      progressRing.style.strokeDashoffset = offset;
    }

    /**
     * Stop current audio hotspot playback
     */
    stopAudioHotspot() {
      var hatteAudio = !!this.currentAudioElement;

      if (this.currentAudioElement) {
        this.currentAudioElement.pause();
        this.currentAudioElement.src = '';
        this.currentAudioElement = null;
      }

      // AP-B/B3 (F2): Manager informieren → Atmo entduckt
      if (hatteAudio && this.hotspotMedia && window.StoryMediaManager) {
        window.StoryMediaManager.notifyStop(this.hotspotMedia);
      }

      if (this.currentAudioHotspot) {
        this.currentAudioHotspot.classList.remove('pano-hotspot--playing');
        this.currentAudioHotspot.classList.remove('pano-hotspot--paused');

        // Reset progress ring
        var progressRing = this.currentAudioHotspot.querySelector('.pano-hotspot__progress-ring');
        if (progressRing) {
          var circumference = 2 * Math.PI * 24;
          progressRing.style.strokeDashoffset = circumference;
        }

        this.currentAudioHotspot = null;
      }

      clearInterval(this.audioProgressInterval);
    }

    handleImageHotspot(args) {
      // User Gesture Gate
      if (!window.StoryMediaManager.userGestureReceived) {
        window.StoryMediaManager.userGestureReceived = true;
      }

      // Validate source
      if (!args.src) {
        console.warn('[Panorama360] Image hotspot missing src');
        return;
      }

      // Check if PhotoSwipe is available
      if (typeof PhotoSwipe === 'undefined' || typeof PhotoSwipeUI_Default === 'undefined') {
        console.error('[Panorama360] PhotoSwipe not loaded - cannot open image lightbox');
        return;
      }

      // Find PhotoSwipe container
      var pswpElement = document.querySelector('.pswp');
      if (!pswpElement) {
        console.error('[Panorama360] PhotoSwipe container (.pswp) not found in DOM');
        return;
      }

      // Build items array (single image)
      // w/h are optional but recommended for proper zoom
      var items = [{
        src: args.src,
        msrc: args.src,  // Thumbnail (same as src for hotspots)
        w: args.w || 1200,  // Default width if not specified
        h: args.h || 800,   // Default height if not specified
        title: args.title || ''
      }];

      // Warn if w/h not specified
      if (!args.w || !args.h) {
        console.warn('[Panorama360] Image hotspot missing w/h - using defaults (1200x800). Add "w" and "h" to hotspot JSON for proper zoom.');
      }

      // PhotoSwipe options
      var options = {
        bgOpacity: 0.95,
        closeOnScroll: false,  // Don't close on scroll in panorama context
        fullscreenEl: true,
        history: false,
        index: 0,
        shareEl: false,
        showHideOpacity: true,
        // No thumbnail bounds animation (we're opening from panorama, not an image element)
        showAnimationDuration: 200,
        hideAnimationDuration: 200
      };

      // Open PhotoSwipe
      var gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
      gallery.init();

      if (this.debugMode) {
        console.log('[Panorama360] Image lightbox opened:', args.src);
      }
    }

    handleVideoHotspot(args) {
      // User Gesture Gate
      if (!window.StoryMediaManager.userGestureReceived) {
        window.StoryMediaManager.userGestureReceived = true;
      }

      // Validate source
      if (!args.src) {
        console.warn('[Panorama360] Video hotspot missing src');
        return;
      }

      // Find video modal
      var modal = document.getElementById('video-modal');
      if (!modal) {
        console.error('[Panorama360] Video modal (#video-modal) not found in DOM');
        return;
      }

      var video = modal.querySelector('.video-modal__player');
      var closeBtn = modal.querySelector('.video-modal__close');
      var backdrop = modal.querySelector('.video-modal__backdrop');

      if (!video) {
        console.error('[Panorama360] Video player element not found in modal');
        return;
      }

      // Stop any other media (via MediaManager)
      if (window.StoryMediaManager && window.StoryMediaManager.currentActiveMedia) {
        window.StoryMediaManager.currentActiveMedia.pause();
      }

      // Pause background audio while video plays
      this.pauseBackgroundAudio();

      // Set video source and show modal
      video.src = args.src;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');

      // Animate in
      var self = this;
      requestAnimationFrame(function() {
        modal.classList.add('video-modal--visible');
        video.play().catch(function(err) {
          if (self.debugMode) {
            console.warn('[Panorama360] Video autoplay failed:', err);
          }
        });
      });

      // Close handlers
      var closeModal = function() {
        video.pause();
        video.src = '';  // Free resource
        modal.classList.remove('video-modal--visible');

        setTimeout(function() {
          modal.hidden = true;
          modal.setAttribute('aria-hidden', 'true');
        }, 300);  // Wait for fade animation

        // Remove event listeners
        closeBtn.removeEventListener('click', closeModal);
        backdrop.removeEventListener('click', closeModal);
        document.removeEventListener('keydown', escHandler);

        // Resume background audio
        self.resumeBackgroundAudio();

        if (self.debugMode) {
          console.log('[Panorama360] Video modal closed');
        }
      };

      var escHandler = function(e) {
        if (e.key === 'Escape') {
          closeModal();
        }
      };

      closeBtn.addEventListener('click', closeModal);
      backdrop.addEventListener('click', closeModal);
      document.addEventListener('keydown', escHandler);

      if (this.debugMode) {
        console.log('[Panorama360] Video modal opened:', args.src);
      }
    }

    handleGotoHotspot(args) {
      // AP-B (V5a): goto-Marker mit targetChapter (pano-tool-1-Vertrag) lösen
      // die stabile Kapitel-ID auf das data-sg-chapter-start-Panel auf und
      // springen per fadeTo — goto/scrollIntoView würde überflogene Panels
      // aktivieren (falsches Visited-Tracking + Audio-Anspiel-Stottern).
      if (args.targetChapter !== undefined && args.targetChapter !== null &&
          String(args.targetChapter) !== '') {
        var chapterId = String(args.targetChapter);
        var idx = this.resolveChapterTarget(chapterId);

        if (idx === -1) {
          console.warn('[Panorama360] goto hotspot: kein Panel mit data-sg-chapter="' + chapterId + '" gefunden');
          return;
        }

        if (window.StoryAPI && typeof window.StoryAPI.fadeTo === 'function') {
          if (this.debugMode) {
            console.log('[Panorama360] fadeTo Kapitel "' + chapterId + '" (Panel ' + idx + ')');
          }
          window.StoryAPI.fadeTo(idx);
        } else if (window.StoryAPI && typeof window.StoryAPI.goto === 'function') {
          console.warn('[Panorama360] fadeTo nicht verfügbar — Fallback goto (Visited-Tracking unscharf)');
          window.StoryAPI.goto(idx);
        } else {
          console.error('[Panorama360] StoryAPI not available');
        }
        return;
      }

      // Bestand (unverändert): Navigation über rohen Panel-Index
      if (typeof args.targetIndex === 'undefined') {
        console.warn('[Panorama360] goto hotspot missing targetIndex');
        return;
      }

      if (window.StoryAPI && typeof window.StoryAPI.goto === 'function') {
        window.StoryAPI.goto(args.targetIndex);

        if (this.debugMode) {
          console.log('[Panorama360] Navigating to panel:', args.targetIndex);
        }
      } else {
        console.error('[Panorama360] StoryAPI.goto not available');
      }
    }

    /**
     * AP-B: Kapitel-ID → Panel-Index (bevorzugt data-sg-chapter-start,
     * sonst erstes Vorkommen des Kapitels — wie sperrgebiet.js).
     */
    resolveChapterTarget(chapterId) {
      var api = window.StoryAPI;
      if (!api || typeof api.getPanels !== 'function') return -1;

      var panels = api.getPanels();
      var fallback = -1;
      for (var i = 0; i < panels.length; i++) {
        var p = panels[i];
        if (p.getAttribute && p.getAttribute('data-sg-chapter') === chapterId) {
          if (p.hasAttribute('data-sg-chapter-start')) return i;
          if (fallback === -1) fallback = i;
        }
      }
      return fallback;
    }

    handleActionHotspot(args) {
      // Whitelist validation
      var ALLOWED_ACTIONS = ['openHub', 'toggleNav'];

      if (!args.actionId || !ALLOWED_ACTIONS.includes(args.actionId)) {
        console.warn('[Panorama360] Invalid actionId:', args.actionId);
        return;
      }

      // Execute action
      if (args.actionId === 'openHub') {
        // TODO: Navigate to hub panel (index 0?)
        if (window.StoryAPI) {
          window.StoryAPI.goto(0);
        }
      } else if (args.actionId === 'toggleNav') {
        // TODO: Toggle navigation visibility
        console.log('[Panorama360] toggleNav action (not implemented)');
      }

      if (this.debugMode) {
        console.log('[Panorama360] Action executed:', args.actionId);
      }
    }

    handleInfoHotspot(args) {
      // Show info tooltip/popover with text content
      // Design: Dark tooltip, X + outside-click to close

      var text = args.text || '';
      var title = args.title || 'Info';

      if (!text) {
        console.warn('[Panorama360] Info hotspot missing text');
        return;
      }

      // Close any existing tooltip
      this.closeInfoTooltip();

      // Create tooltip element
      var tooltip = document.createElement('div');
      tooltip.className = 'pano-info-tooltip';
      tooltip.innerHTML =
        '<div class="pano-info-tooltip__header">' +
          '<span class="pano-info-tooltip__title">' + this.escapeHtml(title) + '</span>' +
          '<button class="pano-info-tooltip__close" aria-label="Schließen">×</button>' +
        '</div>' +
        '<div class="pano-info-tooltip__content">' + this.escapeHtml(text) + '</div>';

      // Add to DOM
      document.body.appendChild(tooltip);
      this.currentInfoTooltip = tooltip;

      // Close button handler
      var self = this;
      var closeBtn = tooltip.querySelector('.pano-info-tooltip__close');
      closeBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        self.closeInfoTooltip();
      });

      // Outside click handler (delayed to prevent immediate close)
      setTimeout(function() {
        self.infoTooltipOutsideClickHandler = function(e) {
          if (!tooltip.contains(e.target)) {
            self.closeInfoTooltip();
          }
        };
        document.addEventListener('click', self.infoTooltipOutsideClickHandler);
      }, 100);

      // ESC key handler
      self.infoTooltipEscHandler = function(e) {
        if (e.key === 'Escape') {
          self.closeInfoTooltip();
        }
      };
      document.addEventListener('keydown', self.infoTooltipEscHandler);

      // Animate in
      requestAnimationFrame(function() {
        tooltip.classList.add('pano-info-tooltip--visible');
      });

      if (this.debugMode) {
        console.log('[Panorama360] Info tooltip opened:', title);
      }
    }

    closeInfoTooltip() {
      if (this.currentInfoTooltip) {
        this.currentInfoTooltip.remove();
        this.currentInfoTooltip = null;
      }

      // Remove event listeners
      if (this.infoTooltipOutsideClickHandler) {
        document.removeEventListener('click', this.infoTooltipOutsideClickHandler);
        this.infoTooltipOutsideClickHandler = null;
      }

      if (this.infoTooltipEscHandler) {
        document.removeEventListener('keydown', this.infoTooltipEscHandler);
        this.infoTooltipEscHandler = null;
      }
    }

    escapeHtml(text) {
      var div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * AP-B/B3 (2026-07-23): globaler Mute vom MediaManager-Fan-out —
     * erreicht Hotspot-Audio UND Pano-Hintergrund-Audio.
     */
    setMuted(muted) {
      this._globalMuted = !!muted;
      if (this.currentAudioElement) this.currentAudioElement.muted = this._globalMuted;
      if (this.backgroundAudio) this.backgroundAudio.muted = this._globalMuted;
    }

    /* ----------------------------------------------------------
       Camera State Persistence
       ---------------------------------------------------------- */

    saveCameraState(viewer) {
      if (!viewer) return null;

      try {
        return {
          yaw: viewer.getYaw(),
          pitch: viewer.getPitch(),
          hfov: viewer.getHfov()
        };
      } catch (error) {
        console.error('[Panorama360] Error saving camera state:', error);
        return null;
      }
    }

    restoreCameraState(viewer, state) {
      if (!viewer || !state) return;

      try {
        viewer.setYaw(state.yaw);
        viewer.setPitch(state.pitch);
        viewer.setHfov(state.hfov);

        if (this.debugMode) {
          console.log('[Panorama360] Camera state restored:', state);
        }
      } catch (error) {
        console.error('[Panorama360] Error restoring camera state:', error);
      }
    }

    /* ----------------------------------------------------------
       Fullscreen + Scroll Blocking
       ---------------------------------------------------------- */

    enableFullscreen(panoSlide) {
      // Add fullscreen class to .pano-slide
      panoSlide.classList.add('pano-slide--fullscreen');

      // NOTE: We do NOT block scroll - scroll is used for panel navigation
      // Panorama zoom is controlled via +/- buttons, not scroll

      this.fullscreenActive = true;

      if (this.debugMode) {
        console.log('[Panorama360] Fullscreen enabled (scroll navigation active)');
      }
    }

    disableFullscreen() {
      // Find all .pano-slide elements and remove fullscreen class
      var panoSlides = document.querySelectorAll('.pano-slide--fullscreen');
      panoSlides.forEach(function(slide) {
        slide.classList.remove('pano-slide--fullscreen');
      });

      // NOTE: Scroll is never blocked, so nothing to restore

      this.fullscreenActive = false;

      if (this.debugMode) {
        console.log('[Panorama360] Fullscreen disabled');
      }
    }

    /* ----------------------------------------------------------
       WebGL Support Check (CACHED to prevent context loss)
       ---------------------------------------------------------- */

    isWebGLSupported() {
      // Return cached result if already checked
      if (this.webglChecked) {
        return this.webglSupported;
      }

      // Check once and cache
      this.webglChecked = true;
      this.webglSupported = this.checkWebGLSupport();

      return this.webglSupported;
    }

    checkWebGLSupport() {
      try {
        var canvas = document.createElement('canvas');
        var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        if (!gl) {
          return false;
        }

        // Optional: log max texture size for debugging (but don't fail on it)
        try {
          var maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
          if (this.debugMode && maxSize) {
            console.log('[Panorama360] WebGL supported. Max texture size:', maxSize);
          }
        } catch (e) {
          // Ignore texture size check errors
        }

        return true;
      } catch (e) {
        return false;
      }
    }

    showErrorMessage(container, message) {
      // Show user-friendly error in viewer container
      container.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: white; text-align: center; padding: 2rem; background: rgba(0,0,0,0.8);">' +
        '<div>' +
        '<h3 style="margin: 0 0 1rem 0;">⚠️ Fehler</h3>' +
        '<p style="margin: 0;">' + message + '</p>' +
        '</div>' +
        '</div>';
    }

    /* ----------------------------------------------------------
       Background Audio (Ambient Sound)
       ---------------------------------------------------------- */

    /**
     * Start background audio if configured on the pano-slide element
     * Uses data-pano-audio-bg for source URL
     * Uses data-pano-audio-volume for volume (0-1, default 0.5)
     */
    startBackgroundAudio() {
      if (!this.element) return;

      var audioSrc = this.element.dataset.panoAudioBg;
      if (!audioSrc) return;  // No background audio configured

      // Check user gesture gate
      if (window.StoryMediaManager && !window.StoryMediaManager.userGestureReceived) {
        if (this.debugMode) {
          console.log('[Panorama360] Background audio skipped - no user gesture yet');
        }
        return;
      }

      // Get target volume
      var volume = parseFloat(this.element.dataset.panoAudioVolume) || this.bgAudioVolume;
      this.bgAudioVolume = Math.max(0, Math.min(1, volume));  // Clamp 0-1

      // Create audio element if not exists
      if (!this.backgroundAudio) {
        this.backgroundAudio = new Audio();
        this.backgroundAudio.loop = true;  // Ambient audio loops
        this.backgroundAudio.preload = 'auto';
      }

      // Set source and start
      this.backgroundAudio.src = audioSrc;
      this.backgroundAudio.volume = 0;  // Start silent for fade-in
      // B3 (Ultrareview bug_005): globaler Mute gilt auch für eine Atmo,
      // die erst NACH dem Mute-Tipp erzeugt wird (Hub vor erstem Pano).
      this.backgroundAudio.muted = !!this._globalMuted;

      var self = this;
      this.backgroundAudio.play()
        .then(function() {
          // Fade in (300ms)
          self.bgAudioFadeIn(self.bgAudioVolume);

          if (self.debugMode) {
            console.log('[Panorama360] Background audio started:', audioSrc, 'volume:', self.bgAudioVolume);
          }
        })
        .catch(function(error) {
          if (self.debugMode) {
            console.warn('[Panorama360] Background audio failed to play:', error);
          }
        });
    }

    /**
     * Stop background audio
     * @param {boolean} useFade - Use fade-out (300ms) or immediate stop
     */
    stopBackgroundAudio(useFade) {
      if (!this.backgroundAudio) return;

      var self = this;

      // Clear any existing fade
      clearInterval(this.bgAudioFadeInterval);

      if (useFade && this.backgroundAudio.volume > 0) {
        // Fade out then stop (300ms)
        this.bgAudioFadeOut(function() {
          self.backgroundAudio.pause();
          self.backgroundAudio.currentTime = 0;
          self.backgroundAudio.src = '';  // Free resource

          if (self.debugMode) {
            console.log('[Panorama360] Background audio stopped (with fade)');
          }
        });
      } else {
        // Immediate stop
        this.backgroundAudio.pause();
        this.backgroundAudio.volume = 0;
        this.backgroundAudio.currentTime = 0;
        this.backgroundAudio.src = '';

        if (this.debugMode) {
          console.log('[Panorama360] Background audio stopped (immediate)');
        }
      }
    }

    /**
     * Fade in background audio
     * @param {number} targetVolume - Target volume (0-1)
     */
    bgAudioFadeIn(targetVolume) {
      clearInterval(this.bgAudioFadeInterval);
      var audio = this.backgroundAudio;
      if (!audio) return;

      audio.volume = 0;

      // 6 steps × 50ms = 300ms fade
      var step = targetVolume / 6;
      var self = this;

      this.bgAudioFadeInterval = setInterval(function() {
        if (audio.volume < targetVolume - step) {
          audio.volume = Math.min(targetVolume, audio.volume + step);
        } else {
          audio.volume = targetVolume;
          clearInterval(self.bgAudioFadeInterval);
        }
      }, 50);
    }

    /**
     * Fade out background audio
     * @param {function} callback - Called when fade complete
     */
    bgAudioFadeOut(callback) {
      clearInterval(this.bgAudioFadeInterval);
      var audio = this.backgroundAudio;
      if (!audio) {
        if (callback) callback();
        return;
      }

      var startVolume = audio.volume;
      if (startVolume <= 0) {
        if (callback) callback();
        return;
      }

      // 6 steps × 50ms = 300ms fade
      var step = startVolume / 6;
      var self = this;

      this.bgAudioFadeInterval = setInterval(function() {
        if (audio.volume > step) {
          audio.volume = Math.max(0, audio.volume - step);
        } else {
          audio.volume = 0;
          clearInterval(self.bgAudioFadeInterval);
          if (callback) callback();
        }
      }, 50);
    }

    /**
     * Pause background audio (for video/other media)
     * Fades out and pauses, but keeps currentTime for resume
     */
    pauseBackgroundAudio() {
      if (!this.backgroundAudio || this.backgroundAudio.paused) return;

      var self = this;
      this.bgAudioFadeOut(function() {
        self.backgroundAudio.pause();
        if (self.debugMode) {
          console.log('[Panorama360] Background audio paused');
        }
      });
    }

    /**
     * Resume background audio (after video/other media closes)
     * Resumes from current position with fade-in
     */
    resumeBackgroundAudio() {
      if (!this.backgroundAudio || !this.backgroundAudio.src) return;
      // B3-Fix (Ultrareview bug_004): NICHT an isPlaying binden — das
      // Hotspot-Routing über requestPlay pausiert den Viewer (isPlaying
      // false), obwohl das Panel aktiv bleibt; die Atmo blieb dann nach
      // Hotspot-Ende dauerhaft stumm. currentPanel trägt die richtige
      // Semantik „Panel ist aktiv" (init setzt es, stop() räumt es).
      if (!this.currentPanel) return;
      if (this.bgAudioMuted) return;  // Don't resume if user muted

      var self = this;
      this.backgroundAudio.play()
        .then(function() {
          self.bgAudioFadeIn(self.bgAudioVolume);
          if (self.debugMode) {
            console.log('[Panorama360] Background audio resumed');
          }
        })
        .catch(function(error) {
          if (self.debugMode) {
            console.warn('[Panorama360] Background audio resume failed:', error);
          }
        });
    }

    /* ----------------------------------------------------------
       iOS Orientation Permission
       ---------------------------------------------------------- */

    /**
     * Check/request orientation permission for iOS 13+
     * First checks if StoryLoader already obtained permission (global)
     * Falls back to requesting directly if needed
     */
    requestOrientationPermission() {
      var self = this;

      // First: Check if StoryLoader already got permission (preferred)
      if (window.StoryOrientationPermission) {
        this.orientationPermission = window.StoryOrientationPermission;

        if (this.debugMode) {
          console.log('[Panorama360] Using global orientation permission:', this.orientationPermission);
        }

        return Promise.resolve(this.orientationPermission);
      }

      // Fallback: Check if DeviceOrientationEvent.requestPermission exists (iOS 13+)
      if (typeof DeviceOrientationEvent === 'undefined') {
        this.orientationPermission = 'granted';
        return Promise.resolve('granted'); // Not iOS, assume granted
      }

      if (typeof DeviceOrientationEvent.requestPermission !== 'function') {
        this.orientationPermission = 'granted';
        return Promise.resolve('granted'); // Not iOS 13+, assume granted
      }

      // Request permission (requires user gesture - may fail if called too late)
      return DeviceOrientationEvent.requestPermission()
        .then(function(permissionState) {
          self.orientationPermission = permissionState;
          window.StoryOrientationPermission = permissionState; // Store globally

          if (self.debugMode) {
            console.log('[Panorama360] Orientation permission obtained:', permissionState);
          }

          return permissionState;
        })
        .catch(function(error) {
          console.warn('[Panorama360] Orientation permission request failed:', error);
          console.warn('[Panorama360] This may happen if not called during user gesture');
          self.orientationPermission = 'denied';
          return 'denied';
        });
    }

    /* ----------------------------------------------------------
       Destroy Module
       ---------------------------------------------------------- */

    destroy() {
      this.cleanup();

      // Unregister from MediaManager
      if (window.StoryMediaManager) {
        window.StoryMediaManager.unregister(this);
      }

      if (this.debugMode) {
        console.log('[Panorama360] Module destroyed');
      }
    }
  }

  /* ----------------------------------------------------------
     2. Expose to Window
     ---------------------------------------------------------- */

  window.StoryModulePanorama360 = PanoramaModule;

})(window);
